/**
 * SYNESTHESIA in the Inspector and the timeline editor (2026-11-05; `.notepads/synesthesia-app-half.md`).
 *
 * - `SynScenePicker`: the scenes as pictures, picked by how they look (like the Hue picker's colours). Shared by the
 *   Synesthesia Button's editor, the timeline's chooser and a cue's Inspector.
 * - `SynActionEditor`: what one press or cue tells Synesthesia: a SCENE, a MOVE (previous / random / next / home, a
 *   playlist step) or a LOOK (a meta control to a value, faded over some beats; a scene's own control 1-8).
 * - `SynesthesiaButtonEditor`: the Button's whole editor: the action and when it lands.
 * - `SynesthesiaGridFields`: the Grid's choices.
 *
 * Every list it offers is OPBridge's (`GET /synesthesia/scenes`: scenes, meta controls, commands), so a control the
 * running OPBridge cannot carry is never offered.
 */

import { createMemo, createSignal, For, onCleanup, onMount, Show } from 'solid-js';
import { editItem, type SynesthesiaGridItem } from '../layout/store.ts';
import type { ButtonItem } from '../layout/projectStore.ts';
import {
  ensureSynList, refreshSynList, synCommands, synMeta, synPicture, synSceneNamed, synScenes, synSummary, synWhere, watchSynList,
} from '../synesthesia/list.ts';
import {
  SYN_MOVES, SYN_PLAYLIST, SYN_WHENS, metaLabel, metaRest, synLabel, synProblem, takesCommand, type SynAction, type SynWhen,
} from '../synesthesia/model.ts';
import { InfoHint } from './InfoHint.tsx';

/** Above this many scenes the picker gets a filter box. */
const FILTER_FROM = 12;

/** Where Synesthesia is, or why it cannot be reached: one line under a picker. */
export function SynStatusLine() {
  const sum = () => synSummary();
  return (
    <div class="syn-pick-status" data-state={sum().state} data-testid="syn-status-line">
      <Show when={sum().state === 'ok'} fallback={<span>{sum().text}</span>}>
        <span>Synesthesia{synWhere() ? ` on ${synWhere()}` : ''}, {synScenes().length} scene{synScenes().length === 1 ? '' : 's'}</span>
      </Show>
      <Show when={sum().state !== 'reading' && sum().state !== 'nobridge'}>
        <button type="button" class="syn-pick-again" onClick={() => void refreshSynList()}>Read again</button>
      </Show>
    </div>
  );
}

/** The scenes as pictures. `selected` is ringed; unmapped ones are dimmed but pickable (a cue can be written before
 *  the mappings are). */
export function SynScenePicker(props: { selected?: string | undefined; onPick: (name: string) => void; testid?: string }) {
  onMount(() => { const release = watchSynList(); onCleanup(release); ensureSynList(); });
  const [filter, setFilter] = createSignal('');
  const shown = createMemo(() => {
    const q = filter().trim().toLowerCase();
    return synScenes().filter((s) => !q || s.name.toLowerCase().includes(q));
  });
  return (
    <div class="syn-pick" data-testid={props.testid}>
      <SynStatusLine />
      <Show when={synScenes().length > FILTER_FROM}>
        <input class="syn-pick-filter" type="search" placeholder="Find a scene" aria-label="Find a scene" value={filter()}
          onInput={(e) => setFilter(e.currentTarget.value)} onKeyDown={(e) => e.stopPropagation()} />
      </Show>
      <Show when={synScenes().length > 0}>
        <div class="syn-pick-scenes">
          <For each={shown()} fallback={<div class="syn-pick-empty">No scene matches "{filter()}".</div>}>
            {(s) => (
              <button
                type="button" class="syn-pick-scene" data-scene={s.name} aria-pressed={props.selected === s.name}
                data-mapped={s.mapped ? 'true' : 'false'}
                title={s.mapped ? s.name : `${s.name}: not mapped in Synesthesia yet (OPBridge writes it when you write the mappings)`}
                onClick={() => props.onPick(s.name)}
              >
                <span class="syn-pick-pic">
                  <Show when={synPicture(s.name)}>{(src) => <img src={src()} alt="" draggable={false} />}</Show>
                </span>
                <span class="syn-pick-name">{s.name}</span>
              </button>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}

type Does = 'scene' | 'move' | 'look';
const doesOf = (a: SynAction): Does => (a.command === 'scene' ? 'scene' : a.command === 'meta' || a.command === 'control' ? 'look' : 'move');

/** What one press or cue tells Synesthesia. `fades` shows the fade (a timeline cue and a button both take one). */
export function SynActionEditor(props: { value: SynAction; onChange: (a: SynAction) => void; testid?: string; lookOnly?: boolean }) {
  onMount(() => { const release = watchSynList(); onCleanup(release); });
  const a = () => props.value;
  const takes = (c: SynAction['command']) => takesCommand(c, synCommands());
  const meta = () => synMeta();
  const metaType = (name: string | undefined) => meta().find((m) => m.name === name)?.type ?? 'slider';
  /** The Look list: the meta controls, then the scene's own controls when OPBridge takes them. */
  const lookOptions = (): Array<{ key: string; label: string }> => [
    ...meta().map((m) => ({ key: `meta:${m.name}`, label: metaLabel(m.name) })),
    ...(takes('control') ? [1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({ key: `control:${n}`, label: `Scene control ${n}` })) : []),
  ];
  const lookKey = () => (a().command === 'control' ? `control:${a().slot ?? 1}` : `meta:${a().control ?? ''}`);
  function setDoes(d: Does): void {
    if (d === doesOf(a())) return;
    if (d === 'scene') props.onChange({ command: 'scene', scene: synScenes().find((s) => s.mapped)?.name ?? '' });
    else if (d === 'move') props.onChange({ command: 'next' });
    else {
      const first = meta().find((m) => m.name === 'hue') ?? meta()[0];
      props.onChange({ command: 'meta', control: first?.name ?? 'hue', value: metaRest(first?.name ?? 'hue', first?.type ?? 'slider') });
    }
  }
  function setLook(key: string): void {
    const [kind, rest] = key.split(':') as [string, string];
    if (kind === 'control') props.onChange({ command: 'control', slot: Number(rest), value: a().value ?? 0.5, ...(a().fadeBeats ? { fadeBeats: a().fadeBeats } : {}) });
    else props.onChange({ command: 'meta', control: rest, value: metaType(rest) === 'toggle' ? 1 : a().value ?? metaRest(rest, metaType(rest)), ...(metaType(rest) !== 'toggle' && a().fadeBeats ? { fadeBeats: a().fadeBeats } : {}) });
  }
  const isToggle = () => a().command === 'meta' && metaType(a().control) === 'toggle';
  const pct = () => Math.round((a().value ?? 0) * 100);
  const FADES: Array<[number, string]> = [[0, 'At once'], [4, '1 bar'], [8, '2 bars'], [16, '4 bars']];
  return (
    <div class="syn-edit" data-testid={props.testid}>
      <Show when={!props.lookOnly}>
        <div class="inspector-pills" data-testid="syn-does">
          <span class="inspector-pills-label">Does</span>
          <button type="button" class="inspector-pill" data-does="scene" data-selected={doesOf(a()) === 'scene' ? 'true' : 'false'} onClick={() => setDoes('scene')}>Scene</button>
          <button type="button" class="inspector-pill" data-does="move" data-selected={doesOf(a()) === 'move' ? 'true' : 'false'} onClick={() => setDoes('move')}>Move</button>
          <button type="button" class="inspector-pill" data-does="look" data-selected={doesOf(a()) === 'look' ? 'true' : 'false'} onClick={() => setDoes('look')}>Look</button>
        </div>
      </Show>

      <Show when={doesOf(a()) === 'scene'}>
        <div class="inspector-field">
          <span>Scene</span>
          <span class="syn-pick-current" data-testid="syn-current">{a().scene || 'Pick one below'}</span>
        </div>
        <SynScenePicker selected={a().scene} onPick={(name) => props.onChange({ command: 'scene', scene: name })} testid="syn-scene-picker" />
        <Show when={!!a().scene && synSummary().state === 'ok' && !synSceneNamed(a().scene)}>
          <span class="inspector-hint">"{a().scene}" is not in Synesthesia's list now: pick another, or add its playlist in OPBridge.</span>
        </Show>
      </Show>

      <Show when={doesOf(a()) === 'move'}>
        <div class="syn-move-list" data-testid="syn-moves">
          <For each={[...SYN_MOVES, ...SYN_PLAYLIST].filter((m) => takes(m.command))}>
            {(m) => (
              <button type="button" class="inspector-pill" data-command={m.command} data-selected={a().command === m.command ? 'true' : 'false'}
                title={`${m.label}: ${m.hint}`} onClick={() => props.onChange({ command: m.command })}>{m.label}</button>
            )}
          </For>
        </div>
      </Show>

      <Show when={doesOf(a()) === 'look'}>
        <label class="inspector-field">
          <span>Control</span>
          <select data-testid="syn-look-control" value={lookKey()} onChange={(e) => setLook(e.currentTarget.value)}>
            <For each={lookOptions()}>{(o) => <option value={o.key}>{o.label}</option>}</For>
          </select>
        </label>
        <Show when={isToggle()} fallback={
          <label class="inspector-field">
            <span>To</span>
            <input type="range" min="0" max="100" step="1" value={pct()} data-testid="syn-look-value"
              onInput={(e) => props.onChange({ ...a(), value: Number(e.currentTarget.value) / 100 })} />
            <span class="syn-look-pct">{pct()}%</span>
          </label>
        }>
          <div class="inspector-pills">
            <span class="inspector-pills-label">To</span>
            <button type="button" class="inspector-pill" data-selected={(a().value ?? 0) >= 0.5 ? 'true' : 'false'} onClick={() => props.onChange({ ...a(), value: 1 })}>On</button>
            <button type="button" class="inspector-pill" data-selected={(a().value ?? 0) < 0.5 ? 'true' : 'false'} onClick={() => props.onChange({ ...a(), value: 0 })}>Off</button>
          </div>
        </Show>
        <Show when={!isToggle()}>
          <div class="inspector-pills" data-testid="syn-fade">
            <span class="inspector-pills-label">Fade</span>
            <For each={FADES}>
              {([b, label]) => (
                <button type="button" class="inspector-pill" data-beats={b} data-selected={(a().fadeBeats ?? 0) === b ? 'true' : 'false'}
                  title={b === 0 ? 'Straight there' : `Glides there over ${b} beats of the clock`}
                  onClick={() => { const { fadeBeats: _f, ...rest } = a(); void _f; props.onChange(b > 0 ? { ...rest, fadeBeats: b } : rest); }}>{label}</button>
              )}
            </For>
          </div>
        </Show>
        <Show when={a().command === 'control'}>
          <span class="inspector-hint">A scene's own controls work once each is taught in Synesthesia (OPBridge's Synesthesia panel, Teach).</span>
        </Show>
      </Show>
    </div>
  );
}

/** The Synesthesia Button's editor: what it does and when it lands. Names an unnamed button after what it does. */
export function SynesthesiaButtonEditor(props: { item: ButtonItem }) {
  const cfg = (): SynAction & { when?: SynWhen } => props.item.synesthesia ?? { command: 'scene', scene: '' };
  const when = (): SynWhen => cfg().when ?? 'bar';
  function change(next: SynAction): void {
    const patch: { synesthesia: SynAction & { when?: SynWhen }; name?: string } = { synesthesia: { ...next, ...(cfg().when ? { when: cfg().when } : {}) } };
    const name = (props.item.name ?? '').trim();
    const before = synLabel(cfg());
    if (!name || name === before) patch.name = next.command === 'scene' ? next.scene ?? '' : synLabel(next);
    editItem(props.item.id, patch);
  }
  return (
    <div class="inspector-zone" data-syn-editor="true">
      <SynActionEditor value={cfg()} onChange={change} testid="syn-button-editor" />
      <div class="inspector-pills" data-testid="syn-when">
        <span class="inspector-pills-label">Starts</span>
        <For each={SYN_WHENS}>
          {([w, label]) => (
            <button type="button" class="inspector-pill" data-when={w} data-selected={when() === w ? 'true' : 'false'}
              title={w === 'bar' ? 'Lands on the next bar line of the clock' : w === 'beat' ? 'Lands on the next beat' : 'Lands the moment you tap'}
              onClick={() => editItem(props.item.id, { synesthesia: { ...cfg(), when: w } })}>{label}</button>
          )}
        </For>
      </div>
      <Show when={synProblem(cfg())}>
        {(p) => <span class="inspector-hint">This button does nothing yet: {p()}.</span>}
      </Show>
      <InfoHint title="What a tap does">
        <p>It goes to Synesthesia through OPBridge, on the PC that runs it, landing on the bar unless you pick Beat or Now.</p>
        <p>Synesthesia does not say what it is playing, so the button shows SENT, never on: it is what this device sent.</p>
      </InfoHint>
    </div>
  );
}

/** The Synesthesia Grid's choices. */
export function SynesthesiaGridFields(props: { item: SynesthesiaGridItem }) {
  onMount(() => { const release = watchSynList(); onCleanup(release); });
  const when = (): SynWhen => props.item.when ?? 'bar';
  const toggle = (label: string, title: string, on: boolean, set: (v: boolean) => void, testid: string) => (
    <label class="inspector-toggle" title={title} data-testid={testid}>
      <input type="checkbox" checked={on} onInput={(e) => set(e.currentTarget.checked)} />
      <span>{label}</span>
    </label>
  );
  return (
    <div class="inspector-zone" data-syn-grid-fields="true">
      <SynStatusLine />
      <div class="inspector-pills" data-testid="syn-grid-when">
        <span class="inspector-pills-label">Starts</span>
        <For each={SYN_WHENS}>
          {([w, label]) => (
            <button type="button" class="inspector-pill" data-when={w} data-selected={when() === w ? 'true' : 'false'}
              onClick={() => editItem(props.item.id, { when: w })}>{label}</button>
          )}
        </For>
      </div>
      <div class="inspector-pills" data-testid="syn-grid-scenes">
        <span class="inspector-pills-label">Scenes</span>
        <button type="button" class="inspector-pill" data-selected={props.item.scenes !== 'own' ? 'true' : 'false'}
          title="Your own scenes and the playlists picked in OPBridge's Synesthesia panel" onClick={() => editItem(props.item.id, { scenes: undefined })}>All</button>
        <button type="button" class="inspector-pill" data-selected={props.item.scenes === 'own' ? 'true' : 'false'}
          title="Only the scenes in your own scene folder" onClick={() => editItem(props.item.id, { scenes: 'own' })}>Mine</button>
      </div>
      <label class="inspector-field" title="How many pictures across. Auto: as large as they can be with every scene on view.">
        <span>Columns</span>
        <select data-testid="syn-grid-cols" value={String(props.item.cols ?? 0)}
          onChange={(e) => { const n = Number(e.currentTarget.value); editItem(props.item.id, { cols: n > 0 ? n : undefined }); }}>
          <option value="0">Auto</option>
          <For each={[2, 3, 4, 5, 6, 7, 8, 10, 12]}>{(n) => <option value={String(n)}>{n}</option>}</For>
        </select>
      </label>
      {toggle('Scene names', 'Each scene\'s name on its picture', props.item.sceneNames !== false, (v) => editItem(props.item.id, { sceneNames: v ? undefined : false }), 'syn-grid-names')}
      {toggle('Previous, Random, Next, Home', 'The moves under the pictures', props.item.showMoves !== false, (v) => editItem(props.item.id, { showMoves: v ? undefined : false }), 'syn-grid-moves')}
      {toggle('Look', 'Synesthesia\'s colour and picture controls: hue, saturation, brightness and the rest', props.item.showLook !== false, (v) => editItem(props.item.id, { showLook: v ? undefined : false }), 'syn-grid-look')}
      {toggle('Playlist', 'Synesthesia\'s own playlist: previous, timer, next', !!props.item.showPlaylist, (v) => editItem(props.item.id, { showPlaylist: v ? true : undefined }), 'syn-grid-playlist')}
      {toggle('Scene controls', 'The playing scene\'s own controls 1-8, each taught once in Synesthesia', !!props.item.showSceneControls, (v) => editItem(props.item.id, { showSceneControls: v ? true : undefined }), 'syn-grid-ctl')}
      <InfoHint title="What it shows">
        <p>A tap on a picture launches that scene, on the bar unless you pick Beat or Now. It shows NEXT BAR until OPBridge sends it, then SENT.</p>
        <p>Synesthesia does not say what it is playing, so nothing here is ever shown as on: SENT is what this device last sent.</p>
        <p>The look sliders go at once and rest where you left them from here.</p>
      </InfoHint>
    </div>
  );
}
